import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { pxToRem } from 'utils/convert';
import { BUTTON_SIZES, PrimaryButton, SecondaryButton } from 'ui/button';
import { LongArrowLeft, LongArrowRight, Upload } from 'ui/icons';
import { Divider } from 'ui/divider';
import { PageHeader } from 'ui/pageHeader';
import moment from 'moment';
import { DATE_FORMAT_FULL_WITH_WEEKDAY } from 'utils/date';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserProjectId } from 'core/userContext';
import { useRestMutation, useRestQuery } from 'utils/hooks/api';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { Panel1Content } from './Panel1';
import { Panel2Content } from './Panel2';
import { Panel3Content } from './Panel3';
import { Panel4Content } from './Panel4';

const ImportListContainer = styled.div`
  transition: all 0.25s;
  padding-right: ${pxToRem(250)};
  padding-left: ${pxToRem(250)};
  height: 100%;
`;

const ProgressTracker = styled.div`
  margin: ${pxToRem(20)};
  padding-left: 0;
  position: relative;
  z-index: 1;
  height: ${pxToRem(100)};
`;

const TrackerSection = styled.div`
  width: 25%;
  display: inline;
  float: left;
  list-style-type: none;
  text-transform: uppercase;
  position: relative;
  font-size: 11px;
  z-index: 1;
  text-align: center;

  &::after {
    position: absolute;
    content: '';
    border-bottom: ${pxToRem(7)} solid #ccc8ca;
    width: 100%;
    top: 100%;
    left: 50%;
    z-index: -1;
  }

  &:last-child {
    &::after {
      content: none;
    }
  }
`;

const TrackerNum = styled.div`
  font-size: 18px;
  font-weight: bold;
  border-radius: ${pxToRem(35)};
  color: #fff;
  width: ${pxToRem(35)};
  text-align: center;
  line-height: ${pxToRem(35)};
  position: relative;
  left: 45%;
  right: 45%;
  top: ${pxToRem(20)};
  z-index: 1;

  &.active {
    background-color: #59c1a7;
  }

  &.completed {
    background-color: #caf3e7;
  }

  &.null {
    background-color: #ccc8ca;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${pxToRem(30)};
`;

const steps = [
  { name: 'Basic Info' },
  { name: 'Choose CSV' },
  { name: 'Review' },
  { name: 'Results' },
];

export const ListImportPage = () => {
  const dispatch = useDispatch();
  const projectID = useSelector(selectUserProjectId);

  const [listId, setListID] = useState(-1);

  const [listName, setListName] = useState(
    `List created on ${moment(new Date()).format(DATE_FORMAT_FULL_WITH_WEEKDAY)}`,
  );
  const updateListName = (value: string): void => {
    setListName(value);
  };

  const [listDescription, setListDescription] = useState('');
  const updateListDescription = (value: string): void => {
    setListDescription(value);
  };

  const [listType, setListType] = useState('Standard');
  const updateLisType = (value: string): void => {
    setListType(value);
  };

  const [isBlankList, setIsBlankList] = useState(false);
  const updateIsBlankList = (value: boolean): void => {
    setIsBlankList(value);
  };

  const [updateExistingUserOnly, setUpdateExistingUserOnly] = useState(false);
  const updateUpdateExistingUserOnly = (value: boolean): void => {
    setUpdateExistingUserOnly(value);
  };

  const [triggerJourney, setTriggerJourney] = useState(false);
  const updateTriggerJourney = (value: boolean): void => {
    setTriggerJourney(value);
  };

  const [csvUploadInput, setCSVUploadInput] = useState('');
  const updateCSVUploadInput = (value: string): void => {
    setCSVUploadInput(value);
  };

  const [csvFileName, setCSVFileName] = useState('');
  const [csvFileSize, setCSVFileSize] = useState('');

  const [csvTextInput, setCSVTextInput] = useState('');
  const updateCSVTextInput = (value: string): void => {
    setCSVTextInput(value);
  };

  const [rowCount, setRowCount] = useState(0);

  const [currentStep, setCurrentStep] = useState(1);
  const goForward = () => setCurrentStep((prevStep) => prevStep + 1);
  const goBackward = () => setCurrentStep((prevStep) => prevStep - 1);
  const goLast = () => setCurrentStep(steps.length);

  const { isLoading, isError, data, mutate: upload } = useRestMutation('/lists/importSubmit');

  useEffect(() => {
    if (!isLoading && data) {
      if (isError) {
        dispatch(addNegativeToast('Cannot Upload'));
      } else {
        dispatch(addPositiveToast('Upload Success'));
        goForward();
        setListID(data.emailList.id);
      }
    }
  }, [isLoading]);

  const submitFormData = () => {
    const formData = new FormData();

    formData.append('projectId', projectID.toString());
    formData.append('name', listName);
    formData.append('description', listDescription);
    formData.append('emailListType', listType);
    formData.append('sendContactAddEvent', (!isBlankList).toString());
    formData.append('updateExistingUsersOnly', updateExistingUserOnly.toString());

    upload(formData);
  };

  const handleNext = useMemo(() => {
    if (currentStep === 3) return submitFormData;
    return isBlankList ? goLast : goForward;
  }, [isBlankList, steps.length, currentStep]);

  const { data: queryResult } = useRestQuery(`/i/lists/byNames?names=${listName}`);
  const didValidateListName = !!queryResult;
  const isNameUnique = !queryResult?.lists.length;
  const shouldShowValidationError = didValidateListName && !isNameUnique;

  useEffect(() => {
    if (shouldShowValidationError) {
      dispatch(addNegativeToast('List name already exists', { timeout: 5000 }));
    }
  }, [shouldShowValidationError]);

  return (
    <div>
      <PageHeader
        breadcrumb={{
          content: 'Lists',
          href: '/lists',
        }}
        documentTitle="List Import"
        documentation={{
          content: 'Documentation',
          href: 'https://support.iterable.com/hc/en-us/articles/205694345-List-Import-Best-Practices',
        }}
      >
        Create List
      </PageHeader>

      <Divider />

      <ImportListContainer>
        <ProgressTracker>
          {steps.map((element, index) => {
            return (
              <TrackerSection key={index}>
                <div>{element.name}</div>
                <TrackerNum
                  className={(() => {
                    if (currentStep === index + 1) return 'active';
                    if (currentStep > index + 1) return 'completed';
                    return 'null';
                  })()}
                >
                  {index + 1}
                </TrackerNum>
              </TrackerSection>
            );
          })}
        </ProgressTracker>

        <div className="stepContent">
          <div className="step1Content" hidden={currentStep !== 1}>
            <Panel1Content
              listName={listName}
              updateListName={updateListName}
              listDescription={listDescription}
              updateListDescription={updateListDescription}
              listType={listType}
              updateListType={updateLisType}
              updateExistingUserOnly={updateExistingUserOnly}
              updateUpdateExistingUserOnly={updateUpdateExistingUserOnly}
              isBlankList={isBlankList}
              updateIsBlankList={updateIsBlankList}
              triggerJourney={triggerJourney}
              updateTriggerJourney={updateTriggerJourney}
            />
          </div>
          <div className="step2Content" hidden={currentStep !== 2}>
            <Panel2Content
              csvUploadInput={csvUploadInput}
              updateCSVUploadInput={updateCSVUploadInput}
              setCSVFileName={setCSVFileName}
              setCSVFileSize={setCSVFileSize}
              csvTextInput={csvTextInput}
              updateCSVTextInput={updateCSVTextInput}
            />
          </div>
          <div className="step3Content" hidden={currentStep !== 3}>
            <Panel3Content
              listName={listName}
              listDescription={listDescription}
              listType={listType}
              triggerJourney={triggerJourney}
              csvUploadInput={csvUploadInput}
              csvTextInput={csvTextInput}
              csvFileName={csvFileName}
              csvFileSize={csvFileSize}
              setRowCount={(count) => {
                setRowCount(count);
                return count;
              }}
            />
          </div>
          <div className="step4Content" hidden={currentStep !== 4}>
            <Panel4Content
              listName={listName}
              listDescription={listDescription}
              listType={listType}
              rowCount={rowCount}
              listId={listId}
            />
          </div>
        </div>

        <Divider />

        <div hidden={currentStep === 4}>
          <ButtonGroup className="buttons">
            <SecondaryButton
              isDisabled={currentStep === 1}
              onClick={goBackward}
              size={BUTTON_SIZES.MEDIUM}
            >
              <LongArrowLeft height={20} width={20} />
              Go Back
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                handleNext();
              }}
              isDisabled={
                currentStep >= steps.length ||
                (currentStep === 2 && !csvUploadInput && !csvTextInput)
              }
              size={BUTTON_SIZES.MEDIUM}
            >
              {currentStep === 3 ? (
                <>
                  <Upload height={30} width={30} />
                  Upload Subscribers
                </>
              ) : (
                <>
                  {' '}
                  Next
                  <LongArrowRight height={20} width={20} />
                </>
              )}
            </PrimaryButton>
          </ButtonGroup>
        </div>
      </ImportListContainer>
    </div>
  );
};
